<template>
  <form class="form-grid" @submit.prevent="submit">
    <legend class="sr-only">Bedrijfsgegevens</legend>
    <div>
      <app-fieldset title="Bedrijfsgegevens" class="company">
        <form-input
          id="name"
          v-model.lazy="v$.name.$model"
          :title="i18n.t('/Company/CompanyForm/Name') + '*'"
          class="name"
          type="text"
          :error-message="
            v$.name.$error ? i18n.t('/Company/CompanyForm/ValidationRequiredField') : ''
          "
        />
        <form-input
          id="phoneNumber"
          v-model.lazy="v$.phoneNumber.$model"
          :title="i18n.t('/Company/CompanyForm/Phonenumber') + '*'"
          class="phoneNumber"
          :error-message="
            v$.phoneNumber.$error ? i18n.t('/Company/CompanyForm/ValidationRequiredField') : ''
          "
          type="text"
        />
        <form-input
          id="emailAddress"
          v-model.lazy="v$.emailAddress.$model"
          :title="i18n.t('/Company/CompanyForm/Emailaddress') + '*'"
          class="emailAddress"
          :error-message="
            v$.emailAddress.$error ? i18n.t('/Company/CompanyForm/ValidationRequiredField') : ''
          "
          type="email"
        />

        <form-input
          id="zipCode"
          v-model.lazy="v$.address.zipCode.$model"
          :title="zipCodeTitle"
          class="zipCode"
          :error-message="
            v$.address.zipCode.$error ? i18n.t('/Company/CompanyForm/ValidationRequiredField') : ''
          "
          type="text"
          @change="validateAddress"
        />

        <form-input
          id="houseNumber"
          v-model.lazy="v$.address.houseNumber.$model"
          :title="i18n.t('/Company/CompanyForm/Housenumber') + '*'"
          class="houseNumber"
          :error-message="
            v$.address.houseNumber.$error
              ? i18n.t('/Company/CompanyForm/ValidationRequiredField')
              : ''
          "
          type="text"
          @change="validateAddress"
        />

        <form-input
          id="houseNumberSuffix"
          v-model.lazy="v$.address.houseNumberSuffix.$model"
          :title="i18n.t('/Company/CompanyForm/HousenumberSuffix')"
          class="houseNumberSuffix"
          type="text"
        />

        <span class="grid-spacing-3" />

        <alert
          v-if="showAddressWarning"
          class="addressNotification"
          :title="i18n.t('/Company/CompanyForm/AddressNotFoundWarning')"
          :text="i18n.t('/Company/CompanyForm/ValidationAddressNotFound')"
          type="warning"
        />

        <form-input
          id="street"
          v-model.lazy="v$.address.street.$model"
          :title="i18n.t('/Company/CompanyForm/Street') + '*'"
          class="street"
          :error-message="
            v$.address.street.$error ? i18n.t('/Company/CompanyForm/ValidationRequiredField') : ''
          "
          type="text"
        />

        <form-input
          id="city"
          v-model.lazy="v$.address.city.$model"
          :title="i18n.t('/Company/CompanyForm/City') + '*'"
          class="city"
          :error-message="
            v$.address.city.$error ? i18n.t('/Company/CompanyForm/ValidationRequiredField') : ''
          "
          type="text"
        />
        <form-input
          id="countryCode"
          v-model.lazy="v$.countryCode.$model"
          :title="i18n.t('/Company/CompanyForm/Countrycode')"
          class="countryCode"
          type="text"
        />

        <horizontal-line class="horizontalLine" />

        <form-input
          id="cocNumber"
          v-model.lazy="v$.cocNumber.$model"
          :title="i18n.t('/Company/CompanyForm/CocNumber')"
          class="cocNumber"
          type="text"
          :error-message="
            v$.cocNumber.$error ? i18n.t('/Company/CompanyForm/ValidationInvalidFormat') : ''
          "
        />
        <form-input
          id="cocEstablishmentNumber"
          v-model.lazy="v$.cocEstablishmentNumber.$model"
          :title="i18n.t('/Company/CompanyForm/CocEstablishmentNumber')"
          class="cocEstablishmentNumber"
          type="text"
          :error-message="
            v$.cocEstablishmentNumber.$error
              ? i18n.t('/Company/CompanyForm/ValidationInvalidFormat')
              : ''
          "
        />
      </app-fieldset>
    </div>

    <app-button
      data-qa="company-save-button"
      :disabled="v$.$errors.length > 0 || saving"
      type="submit"
      button-type="cta"
      class="submit-button"
    >
      {{ i18n.t('/Common/Save') }}
    </app-button>
  </form>
</template>

<script>
import {
  required,
  numeric,
  email,
  alpha,
  helpers,
  minLength,
  maxLength,
  requiredIf,
  requiredUnless,
} from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import HorizontalLine from '@/components/generic/presentational/atoms/horizontalLine/HorizontalLine.vue'

import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import StoreConnector from '@/helpers/StoreConnector'
import AddressValidation from '@/api/server/AddressValidation'
import Companies from '@/api/server/Companies'
import Alert from '@/components/generic/presentational/atoms/alert/Alert.vue'

import { mapGetters } from 'vuex'
import CustomerSearchHelper from '@/helpers/CustomerSearchHelper'
import { extend } from 'lodash'

const isDutchZipcode = helpers.withParams({ type: 'isDutchZipcode' }, function (value) {
  if (this.isCountryCodeNL) {
    return helpers.regex(/^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/)(value)
  }
  return true
})

const isPhonenumber = helpers.regex(/^(\+?[\d-*]{1,15})$/)

export default {
  components: {
    AppFieldset,
    FormInput,
    AppButton,
    HorizontalLine,
    Alert,
  },
  props: {
    companyToEdit: {
      type: Object,
      default: null,
    },
  },
  emits: ['onSubmit'],
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      name: null,
      cocEstablishmentNumber: null,
      cocNumber: null,
      countryCode: 'NL',
      emailAddress: null,
      fleetSalesCustomer: null,
      magicNumber: null,
      mailingAddress: null,
      mailingZipcodeAndCity: null,
      phoneNumber: null,
      poId: null,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,

      address: {
        zipCode: null,
        houseNumber: null,
        houseNumberSuffix: '',
        city: null,
        street: null,
      },

      hasValidAddress: false,
      saving: false,
    }
  },
  computed: {
    ...mapGetters('quote', ['customerCompany']),
    showAddressWarning() {
      return (
        !this.hasValidAddress &&
        !this.fetchAddressInProgress &&
        this.v$.address.zipCode.$dirty &&
        this.v$.address.houseNumber.$dirty
      )
    },
    isCreateForm() {
      return this.companyToEdit == null
    },
    isCountryCodeNL() {
      return this.countryCode === 'NL'
    },
    zipCodeTitle() {
      const zipCodeLabel = this.i18n.t('/Company/CompanyForm/Zipcode')
      const asterisk = this.isCountryCodeNL ? '*' : ''

      return `${zipCodeLabel} ${asterisk}`
    },
  },
  created() {
    // prefill fields
    if (this.companyToEdit != null) {
      this.countryCode = this.companyToEdit.CustomerCompanyCountryCode
      this.name = this.companyToEdit.CustomerCompanyName
      this.phoneNumber = this.companyToEdit.CustomerCompanyPhoneNumber
      this.emailAddress = this.companyToEdit.CustomerCompanyEmailAddress
      this.cocNumber = this.companyToEdit.CustomerCompanyChamberOfCommerceNumber
      this.cocEstablishmentNumber =
        this.companyToEdit.CustomerCompanyChamberOfCommerceEstablishmentNumber
      this.address.zipCode = this.companyToEdit.CustomerCompanyZipcode
      this.address.city = this.companyToEdit.CustomerCompanyCity
      this.prefillStreetAndHouseNumber(this.companyToEdit.CustomerCompanyAddress)
      this.$nextTick(() => {
        this.v$.$touch()
      })
    }
  },
  methods: {
    async submit() {
      this.saving = true
      this.validateFields()

      if (this.v$.$errors.length > 0) {
        this.saving = false
        return
      }

      let quoteCompany = {
        CustomerCompanyName: this.name,
        CustomerCompanyPhoneNumber: this.phoneNumber,
        CustomerCompanyEmailAddress: this.emailAddress,
        CustomerCompanyChamberOfCommerceEstablishmentNumber: this.cocEstablishmentNumber,
        CustomerCompanyChamberOfCommerceNumber: this.cocNumber,
        CustomerCompanyCountryCode: this.countryCode,
        CustomerCompanyAddress:
          this.address.street + ' ' + this.address.houseNumber + this.address.houseNumberSuffix,
        CustomerCompanyCity: this.address.city,
        CustomerCompanyZipcode: this.address.zipCode,
        CustomerCompanySource: 'CMDM',
      }

      if (this.isCreateForm) {
        const res = await Companies.createCompany(quoteCompany)
        quoteCompany.CustomerCompanyPoId = res
        StoreConnector.dispatch('quote/setCustomerCompany', quoteCompany)
      } else {
        quoteCompany = extend(this.companyToEdit, quoteCompany)
        StoreConnector.dispatch('quote/createOrUpdate')
      }

      this.saving = false
      this.$emit('onSubmit')
    },
    validateFields() {
      this.v$.name.$touch()
      this.v$.phoneNumber.$touch()
      this.v$.emailAddress.$touch()
      this.v$.cocNumber.$touch()
      this.v$.cocEstablishmentNumber.$touch()
      this.v$.countryCode.$touch()
      this.v$.address.$touch()
    },
    async getAddress() {
      try {
        this.fetchAddressInProgress = true
        const addresses = await AddressValidation.getAddressByZipcodeAndHousenumber(
          this.address.zipCode,
          this.address.houseNumber
        )

        if (addresses != null && addresses.length > 0) {
          this.address.city = addresses[0].City
          this.address.street = addresses[0].Street
          this.hasValidAddress = true
        } else {
          this.hasValidAddress = false
        }
        this.fetchAddressInProgress = false
      } catch (e) {
        this.hasValidAddress = false
      }
    },
    async validateAddress() {
      this.v$.address.zipCode.$touch()
      this.v$.address.houseNumber.$touch()

      if (
        this.v$.address.zipCode.$dirty &&
        !this.v$.address.zipCode.$error &&
        this.v$.address.houseNumber.$dirty &&
        !this.v$.address.houseNumber.$error
      ) {
        await this.getAddress()
      }
    },
    prefillStreetAndHouseNumber(address) {
      if (address) {
        let parsedAddress = CustomerSearchHelper.parseAddress(address)
        this.address.street = parsedAddress.street
        this.address.houseNumber = parsedAddress.houseNumber
        this.address.houseNumberSuffix = parsedAddress.houseNumberAddition
        this.getAddress()
      }
    },
  },
  validations: {
    name: {
      required,
    },
    phoneNumber: {
      isPhonenumber,
      required: requiredUnless((value, model) => {
        return !!model.emailAddress
      }),
    },
    emailAddress: {
      email,
      required: requiredUnless((value, model) => {
        return !!model.phoneNumber
      }),
    },
    cocNumber: {
      numeric,
      minLength: minLength(8),
      maxLength: maxLength(8),
    },
    cocEstablishmentNumber: {
      numeric,
      minLength: minLength(12),
      maxLength: maxLength(12),
    },
    countryCode: {
      alpha,
    },
    address: {
      zipCode: {
        required: requiredIf(function () {
          this.isCountryCodeNL
        }),
        isDutchZipcode,
      },
      houseNumber: {
        required,
        numeric,
      },
      houseNumberSuffix: {
        alpha,
      },
      city: {
        required,
      },
      street: {
        required,
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.company {
  :deep(.fields) {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
  }
}

.name,
.phoneNumber,
.emailAddress,
.city,
.street {
  grid-column: span 2;
}

.cocNumber,
.cocEstablishmentNumber,
.grid-spacing-3 {
  grid-column: span 3;
}

.addressNotification,
.horizontalLine {
  grid-column: span 6;
}
</style>
